import { Link } from "react-router-dom";
import { roundTo } from "../../utils/mathUtils";

export const r3 = (val) => (val != null ? roundTo(Number(val), 3) : "—");
export const r1 = (val) => (val != null ? roundTo(Number(val), 1) : "—");
export const pct1 = (val) => (val != null ? `${roundTo(Number(val), 1)}%` : "—");

export const rankCol = () => ({
  name: "#",
  selector: (row) => row.rank,
  sortable: true,
  width: "4rem",
  className: "text-center",
});

export const playerCol = () => ({
  name: "Player",
  selector: (row) => row.player_name,
  sortable: true,
  width: "10rem",
  cell: (row) =>
    String(row.player_id).substring(0, 4) === "d3d-" ? (
      <Link to={`/player/${row.player_id}`} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
        {row.player_name}
      </Link>
    ) : (
      <span className="font-medium">{row.player_name}</span>
    ),
});

export const teamCol = (width = "10rem") => ({
  name: "Team",
  selector: (row) => row.team_name,
  sortable: true,
  width,
  cell: (row) => {
    const orgId = row.org_id || row.team_org_id;
    return orgId ? (
      <Link to={`/team/${orgId}`} className="text-blue-600 hover:text-blue-800 hover:underline">
        {row.team_name}
      </Link>
    ) : (
      <span>{row.team_name}</span>
    );
  },
});

export const teamNameCol = (width = "10rem") => ({
  name: "Team",
  selector: (row) => row.team_name,
  sortable: true,
  width,
  cell: (row) => {
    const orgId = row.org_id || row.team_org_id;
    return orgId ? (
      <Link to={`/team/${orgId}`} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
        {row.team_name}
      </Link>
    ) : (
      <span className="font-medium">{row.team_name}</span>
    );
  },
});

export const confCol = (width = "8rem") => ({
  name: "Conference",
  selector: (row) => row.conference,
  sortable: true,
  width,
});

export const yearCol = () => ({
  id: "year",
  name: "Year",
  selector: (row) => row.year,
  sortable: true,
  width: "5rem",
  className: "text-center",
});

export const numCol = (name, field, width = "5rem") => ({
  name,
  selector: (row) => row[field],
  sortable: true,
  width,
  className: "text-center",
});

export const round3Col = (name, field, width = "6.5rem") => ({
  name,
  selector: (row) => row[field],
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => r3(row[field]),
});

export const round1Col = (name, field, width = "6rem") => ({
  name,
  selector: (row) => row[field],
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => r1(row[field]),
});

export const pctCol = (name, field, width = "6rem") => ({
  name,
  selector: (row) => row[field],
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => pct1(row[field]),
});

export const decPctCol = (name, field, width = "6rem") => ({
  name,
  selector: (row) => row[field] != null ? Number(row[field]) : null,
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => row[field] != null ? `${roundTo(Number(row[field] * 100), 1)}%` : "—",
});

export const warCol = (width = "6rem") => ({
  name: "WAR",
  selector: (row) => row.war,
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => r1(row.war),
});

export const sosAdjWarCol = (width = "7rem") => ({
  name: "SOS Adj WAR",
  selector: (row) => row.sos_adj_war,
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => r1(row.sos_adj_war),
});

export const divCol = (width = "6rem") => ({
  name: "Division",
  selector: (row) => row.division,
  sortable: true,
  width,
  className: "text-center",
});

export const classCol = (width = "6rem") => ({
  name: "Class",
  selector: (row) => row.class,
  sortable: true,
  width,
  className: "text-center",
});

export const btCol = (width = "6rem") => ({
  name: "B/T",
  selector: (row) => (row.bats && row.throws) ? row.bats.charAt(0).toUpperCase() + "/" + row.throws.charAt(0).toUpperCase() : "-",
  sortable: true,
  width,
  className: "text-center",
});

export const throwsCol = (width = "5rem") => ({
  name: "Throws",
  selector: (row) => row.throws ? row.throws.charAt(0).toUpperCase() : '-',
  sortable: true,
  width,
  className: "text-center",
});

export const round0Col = (name, field, width = "5rem") => ({
  name,
  selector: (row) => row[field],
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => row[field] != null ? roundTo(Number(row[field]), 0) : "—",
});

export const round2Col = (name, field, width = "5rem") => ({
  name,
  selector: (row) => row[field],
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => row[field] != null ? roundTo(Number(row[field]), 2) : "—",
});

const createPercentageSortFn = (field) => (rowA, rowB) => {
  const a = parseFloat(String(rowA[field]).replace("%", "")) || 0;
  const b = parseFloat(String(rowB[field]).replace("%", "")) || 0;
  return a - b;
};

export const pctColWithSort = (name, field, width = "5rem") => ({
  name,
  selector: (row) => row[field],
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => pct1(row[field]),
  sortFunction: createPercentageSortFn(field),
});

export const opsCol = (width = "5rem") => ({
  name: "OPS",
  selector: (row) => row.ob_pct + row.slg_pct,
  sortable: true,
  width,
  className: "text-center",
  cell: (row) => r3((row.ob_pct || 0) + (row.slg_pct || 0)),
});